import React, { useMemo, useState, Fragment } from "react";
import classnames from "classnames";
import ReactSwitch from "react-switch";
import { toast } from "react-toastify";
import { EyeIcon, PencilIcon, TrashIcon } from "@aureskonnect/react-ui";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { uuid } from "uuidv4";

import { store as device } from "../../../../components/VerticalLayout/store";
import store, {
    setSelectedDevise,
    setDevise,
    setIsActionsClicked,
    setIsEdited,
    setActions,
    setIsLoading,
    setKey,
} from "../store";

import ErrorToast from "@components/Common/ErrorTost";
import TimePastSvgIcon from "@components/Common/SvgIcons/TimePastSvgIcon";
import { ConfirmationArchive } from "../ConfirmationArchive";
import ConsultSvgIcon from "@components/Common/SvgIcons/ConsultSvgIcon";
import {
    setActivationDeactivationMessageModalOpened,
    setMessage,
    setYesFunction,
} from "@components/Common/ConfirmationActivationDeactivationMessageModal/store";
import { useFetchGlobal } from "../../../../api/hooks";
import { showSuccessToast } from "@components/Common/SuccessToast";

export function CustomIcons(props: any) {
    const {
        e,
        setShopId,
        setDevises,
        setDataIsUpdated,
        setIsHistoryModalOpened,
        setSelectedRows,
        setIsAddDeviceClicked,
        isAddDeviceClicked,
        setIsModalConfirmationOpened,
        isModalConfirmationOpened,
        isDeviseArchived,
        dataArchived,
        selectedRows,
        setLocalFilterActive,
        setIsConsult,
    } = props;

    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const {
        shopsDeviceData,
        deviceDataData,
        isActionsClicked,
        isEdited,
        modify,
        isLoading,
    } = useSnapshot(store);
    const { franchiseID, oneShop, userID, operatorID } = useSnapshot(device);

    const [choiceState, setDeviseState] = useState<boolean>(
        e.selectedRow.isActive
    );
    const [isArchiveModalOpened, setIsArchiveModalOpened] = useState(false);

    const uuidUser = localStorage.getItem("uuidUser");

    const dataArchive = useMemo(() => {
        if (!oneShop) {
            const ids = shopsDeviceData?.flatMap(
                (el: any) => el.subRows?.map((sub: any) => sub.id) ?? []
            );
            return [...new Set(ids)];
        }
        const ids = dataArchived?.data?.map((d: any) => d.id) ?? [];
        return [...new Set(ids)];
    }, [oneShop, shopsDeviceData, dataArchived]);

    const buildPayload = (isActive: boolean, row = e.selectedRow) => ({
        userId: userID,
        shopId: [row.idShop],
        franchiseId: franchiseID,
        operatorId: operatorID,
        uuidUser,
        data: {
            [row.id]: {
                Nom_d_Affichage: row[t("Display name")],
                tag: row[t("Tag")],
                symbole: row[t("Symbol")],
                Nbr_décimale: row[t("Nbr decimal")],
                designation_décimale: row[t("Decimal designation")],
                devise: row[t("Currency")],
                pays: row[t("Country")],
                shopId: row.shopId,
                isArchived: false,
                isActive,
            },
        },
    });

    const handleTrashButtonClicked = () => {
        const isAssigned = !oneShop && dataArchive.includes(e.selectedRow.id);

        if (isAssigned) {
            ErrorToast(
                t(
                    "Please note that you cannot archive an item assigned to stores."
                )
            );
            return setIsLoading(false);
        }
        setIsLoading(false);
        setIsArchiveModalOpened(true);
    };

    const handleStateChange = async (state: boolean) => {
        const payload = buildPayload(state);

        try {
            const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/devise`;
            const res: any = await fetchGlobal(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (res.error) throw new Error();

            showSuccessToast(
                state
                    ? t("Currency has been successfully activated")
                    : t("Currency has been successfully deactivated")
            );

            setDataIsUpdated(true);
        } catch (error) {
            ErrorToast(t("There's an error"));
        } finally {
            setIsLoading(false);
        }
    };

    const activeDevise = async () => {
        toast.dismiss();

        const payload = buildPayload(e.selectedRow.isArchived);

        try {
            const apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/general/devise`;

            const res: any = await fetchGlobal(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (res.error) throw new Error();

            showSuccessToast(t("The Currency was successfully unarchived"));
            setKey(uuid());
        } catch {
            ErrorToast(t("There's an error"));
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenForm = (row: any, consult = false) => {
        const apply = () => {
            setIsEdited(false);
            setDevises(row);
            setShopId(row.idShop);
            setLocalFilterActive(false);

            if (!isAddDeviceClicked) setIsAddDeviceClicked(true);
            if (consult) setIsConsult(true);
        };

        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(apply);
        } else {
            apply();
        }
    };

    const row = e.selectedRow;

    if (row.subRows) return null;

    return (
        <Fragment>
            <div className="d-flex justify-content-around">
                {!modify && (
                    <Tooltip title={t("To consult")}>
                        <ConsultSvgIcon
                            className="pointer__clz afa_icn_toConsult"
                            height={35}
                            width={35}
                            style={{
                                cursor: "pointer",
                                position: "relative",
                                bottom: "5px",
                            }}
                            fill="black"
                            onClick={() => handleOpenForm(row, true)}
                        />
                    </Tooltip>
                )}

                {modify && (
                    <>
                        {/* EDIT */}
                        <Tooltip title={isDeviseArchived ? "" : t("Edit")}>
                            <PencilIcon
                                onClick={() => handleOpenForm(row)}
                                height={25}
                                width={25}
                                style={{ cursor: "pointer" }}
                                className={classnames(
                                    "pointer__clz cmn_icn_FRedit",
                                    {
                                        "not-allowed-icon__clz":
                                            isDeviseArchived || isLoading,
                                    },
                                    oneShop ? "mr-1" : "ml-3"
                                )}
                            />
                        </Tooltip>

                        {oneShop && (
                            <Tooltip
                                title={
                                    isDeviseArchived
                                        ? ""
                                        : choiceState
                                        ? t("Unactivate")
                                        : t("Activate")
                                }
                            >
                                <span>
                                    <ReactSwitch
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        handleDiameter={26}
                                        offColor="#f7b4b8"
                                        offHandleColor="#E30613"
                                        checked={choiceState}
                                        onChange={(val) => {
                                            setIsLoading(true);
                                            setMessage(
                                                choiceState
                                                    ? t(
                                                          "Caution, the currency may be used by other modules.\n Would you like to confirm the deactivation of the selected currency?"
                                                      )
                                                    : t(
                                                          "Would you like to confirm the activation of the selected currency?"
                                                      )
                                            );
                                            setYesFunction(() => {
                                                setDeviseState(!choiceState);
                                                handleStateChange(val);
                                            });
                                            setActivationDeactivationMessageModalOpened(
                                                true
                                            );
                                        }}
                                        onColor="#c2eddd"
                                        onHandleColor="#34C38F"
                                        width={50}
                                        height={20}
                                        className={classnames(
                                            "pointer__clz",
                                            {
                                                "not-allowed-icon__clz":
                                                    isDeviseArchived ||
                                                    isLoading,
                                            },
                                            oneShop ? "mr-1" : "ml-3"
                                        )}
                                    />
                                </span>
                            </Tooltip>
                        )}

                        {!isDeviseArchived ? (
                            <Tooltip title={t("Archive")}>
                                <TrashIcon
                                    onClick={handleTrashButtonClicked}
                                    height={25}
                                    width={25}
                                    fill="red"
                                    className={classnames(
                                        "pointer__clz cmn_icn_FRarchived",
                                        {
                                            "not-allowed-icon__clz": isLoading,
                                        },
                                        oneShop ? "mr-1" : "ml-3"
                                    )}
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip
                                title={
                                    dataArchive.includes(row.id) && oneShop
                                        ? ""
                                        : t("Unarchive")
                                }
                            >
                                <EyeIcon
                                    height={25}
                                    width={25}
                                    className={classnames(
                                        "pointer__clz cmn_icn_FRunarchive",
                                        {
                                            "not-allowed-icon__clz":
                                                isLoading ||
                                                (dataArchive.includes(row.id) &&
                                                    oneShop),
                                        },
                                        oneShop ? "mr-1" : "ml-3"
                                    )}
                                    onClick={async () => {
                                        setIsLoading(true);

                                        if (
                                            !oneShop &&
                                            deviceDataData?.othersData
                                                ?.dataFranchise?.data?.length >
                                                0
                                        ) {
                                            ErrorToast(
                                                t(
                                                    "Attention,the franchise cannot have more than one Device"
                                                )
                                            );
                                            return setIsLoading(false);
                                        }

                                        if (
                                            dataArchive.includes(row.id) &&
                                            !oneShop
                                        ) {
                                            await activeDevise();
                                            setIsModalConfirmationOpened(
                                                !isModalConfirmationOpened
                                            );
                                            return setIsLoading(false);
                                        }

                                        const shopHasDevice = deviceDataData.othersData?.allDevise?.data?.some(
                                            (el: any) =>
                                                el.idShop === row.idShop &&
                                                el.subRows?.length > 0
                                        );

                                        if (oneShop && shopHasDevice) {
                                            ErrorToast(
                                                t(
                                                    "Attention,a shop cannot have more than one currency"
                                                )
                                            );
                                            return setIsLoading(false);
                                        }

                                        await activeDevise();
                                        setDevise(row);
                                        setSelectedRows([row]);
                                    }}
                                />
                            </Tooltip>
                        )}
                    </>
                )}

                <Tooltip title={t("Consult history")}>
                    <TimePastSvgIcon
                        onClick={() => {
                            const open = () => {
                                setIsAddDeviceClicked(false);
                                setIsHistoryModalOpened(true);
                                setSelectedDevise([{ [row.idShop]: row.id }]);
                                setSelectedRows([row]);
                            };

                            if (isEdited) {
                                setIsActionsClicked(!isActionsClicked);
                                setActions(open);
                            } else {
                                open();
                            }
                        }}
                        className={classnames(
                            "pointer__clz cmn_icn_FRhistory",
                            oneShop ? "mr-1" : "ml-3",
                            {
                                "not-allowed-icon__clz": isLoading,
                            }
                        )}
                    />
                </Tooltip>
            </div>

            {isArchiveModalOpened && (
                <ConfirmationArchive
                    isModalOpened={isArchiveModalOpened}
                    setIsModalOpened={setIsArchiveModalOpened}
                    e={e}
                    setDataIsUpdated={setDataIsUpdated}
                    selectedRows={selectedRows}
                />
            )}
        </Fragment>
    );
}
